import React, { useState, useEffect } from 'react';
import { 
  Tag, Plus, Edit2, Trash2, Percent, DollarSign,
  Calendar, Loader2, Check, X, Gift
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPOSDiscounts() {
  const { token } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discount_type: 'PERCENTAGE',
    discount_value: '',
    min_order_amount: '',
    max_discount: '',
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pos/discounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDiscounts(data);
    } catch (error) {
      toast.error('Error loading discounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingDiscount 
        ? `${API_URL}/api/admin/pos/discounts/${editingDiscount._id}`
        : `${API_URL}/api/admin/pos/discounts`;
      
      const response = await fetch(url, {
        method: editingDiscount ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value) || 0,
          min_order_amount: parseFloat(formData.min_order_amount) || 0,
          max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success(editingDiscount ? 'Discount updated' : 'Discount created');
      setShowModal(false);
      resetForm();
      fetchDiscounts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/pos/discounts/${discountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch (error) {
      toast.error('Error deleting discount');
    }
  };

  const toggleActive = async (discount) => {
    try {
      await fetch(`${API_URL}/api/admin/pos/discounts/${discount._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !discount.is_active })
      });
      toast.success(`Discount ${discount.is_active ? 'deactivated' : 'activated'}`);
      fetchDiscounts();
    } catch (error) {
      toast.error('Error updating discount');
    }
  };

  const openEditModal = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      name: discount.name,
      code: discount.code,
      discount_type: discount.discount_type,
      discount_value: discount.discount_value.toString(),
      min_order_amount: discount.min_order_amount?.toString() || '',
      max_discount: discount.max_discount?.toString() || '',
      valid_from: discount.valid_from ? discount.valid_from.split('T')[0] : '',
      valid_until: discount.valid_until ? discount.valid_until.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingDiscount(null);
    setFormData({
      name: '',
      code: '',
      discount_type: 'PERCENTAGE',
      discount_value: '',
      min_order_amount: '',
      max_discount: '',
      valid_from: '',
      valid_until: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pos-discounts">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">POS Discounts</h1>
          <p className="text-gray-500">Manage discount codes for POS billing</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Discount
        </Button>
      </div>

      {/* Discounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {discounts.map((discount) => (
          <div 
            key={discount._id} 
            className={`bg-white rounded-xl border shadow-sm p-5 ${
              !discount.is_active ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  discount.discount_type === 'PERCENTAGE' 
                    ? 'bg-violet-100' 
                    : 'bg-emerald-100'
                }`}>
                  {discount.discount_type === 'PERCENTAGE' ? (
                    <Percent className="w-6 h-6 text-violet-600" />
                  ) : (
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{discount.name}</h3>
                  <p className="text-lg font-bold font-mono text-emerald-600">{discount.code}</p>
                </div>
              </div>
              <button
                onClick={() => toggleActive(discount)}
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  discount.is_active 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {discount.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>

            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Discount</span>
                <span className="font-medium">
                  {discount.discount_type === 'PERCENTAGE' 
                    ? `${discount.discount_value}%`
                    : `₹${discount.discount_value}`
                  }
                </span>
              </div>
              {discount.min_order_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Min Order</span>
                  <span className="font-medium">₹{discount.min_order_amount}</span>
                </div>
              )}
              {discount.max_discount && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Discount</span>
                  <span className="font-medium">₹{discount.max_discount}</span>
                </div>
              )}
              {discount.valid_until && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Expires</span>
                  <span className="font-medium">
                    {new Date(discount.valid_until).toLocaleDateString('en-IN')}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Times Used</span>
                <span className="font-medium">{discount.usage_count || 0}</span>
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => openEditModal(discount)}
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(discount._id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        
        {discounts.length === 0 && (
          <div className="col-span-full bg-white rounded-xl border shadow-sm p-12 text-center">
            <Gift className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Discounts Yet</h3>
            <p className="text-gray-500 mb-4">Create your first discount code for POS billing</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Discount
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Tag className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold">
                  {editingDiscount ? 'Edit Discount' : 'Create Discount'}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Summer Sale 10%"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="SUMMER10"
                  className="font-mono"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="h-10 w-full px-3 rounded-lg border bg-white"
                  >
                    <option value="PERCENTAGE">Percentage (%)</option>
                    <option value="FIXED">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    placeholder={formData.discount_type === 'PERCENTAGE' ? '10' : '100'}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Order (₹)</label>
                  <Input
                    type="number"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData({...formData, min_order_amount: e.target.value})}
                    placeholder="500"
                  />
                </div>
                {formData.discount_type === 'PERCENTAGE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                    <Input
                      type="number"
                      value={formData.max_discount}
                      onChange={(e) => setFormData({...formData, max_discount: e.target.value})}
                      placeholder="200"
                    />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingDiscount ? 'Update' : 'Create'} Discount
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
